/**
 * An account id paired with its display name, as returned by each product's
 * account-name service. The id is typed loosely on purpose: DDC ids are
 * strings, Autotrader's are numbers, and the Autotrader service has been seen
 * returning its numeric ids as strings.
 */
export interface ResolvedAccountName {
  accountId: string | number;
  accountName?: string;
}

/**
 * The key an account is matched on. Ids are compared as lower-cased strings so
 * a stored id still finds its name when the service returns it with different
 * casing or as a string instead of a number.
 */
export function toAccountKey(accountId: string | number): string {
  return accountId.toString().toLowerCase();
}

export type AccountNamesById = ReadonlyMap<string, string | undefined>;

export function mapAccountNamesById(
  resolvedAccountNames?: readonly ResolvedAccountName[]
): AccountNamesById {
  return new Map(
    (resolvedAccountNames ?? []).map(account => [
      toAccountKey(account.accountId),
      account.accountName
    ])
  );
}

export function getAccountName(
  accountNamesById: AccountNamesById,
  accountId: string | number
): string | undefined {
  return accountNamesById.get(toAccountKey(accountId));
}

/**
 * Orders accounts alphabetically by resolved name, case-insensitively. Accounts
 * whose name has not resolved sort after the named ones and keep the order
 * they were supplied in (the sort is stable), rather than being interleaved by
 * their id. Sorting on an id fallback would order a list by id and then
 * reshuffle it once the names land.
 *
 * Shared by the navbar account menu and the account-search Favorites tab so
 * the same favorites always appear in the same order on both.
 */
export function compareAccountsByName(accountNamesById: AccountNamesById) {
  return (a: string | number, b: string | number) => {
    const aName = getAccountName(accountNamesById, a)?.toLowerCase();
    const bName = getAccountName(accountNamesById, b)?.toLowerCase();
    if (aName && bName) return aName.localeCompare(bName);
    if (aName) return -1;
    if (bName) return 1;
    return 0;
  };
}
